import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, Check, Clock, CheckCircle, Building2,
  ChevronDown, ChevronUp, Scale, XCircle, SlidersHorizontal, Sparkles, Info, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { TranslatedFromEnglishBadge } from '../../components/LanguageBadge';
import { formatDateLocale } from '../../utils/dateFormat';
import api from '../../utils/api';
import { formatMoneyPrecise, currencySymbol } from '../../utils/money';
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug)
import TreatmentReadyPanel from '../../components/treatment-ready/TreatmentReadyPanel';
import { apiErrorCode, apiErrorMessage, ELIGIBILITY_CODES } from '../../utils/apiError';


const CURRENCY_SYMBOL = { EUR: '\u20ac', USD: '$', GBP: '\u00a3', TRY: '\u20ba' };
const sym = (c) => CURRENCY_SYMBOL[c] || c + ' ';

const STATUS_STYLE = {
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  EXPIRED: 'bg-red-100 text-red-600',
  REJECTED: 'bg-slate-100 text-slate-500',
};

const INSIGHT_STYLE = {
  'Best match for your priorities': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Lowest price': 'bg-blue-100 text-blue-700 border-blue-200',
  'Fastest response': 'bg-amber-100 text-amber-700 border-amber-200',
  'Highest rated': 'bg-purple-100 text-purple-700 border-purple-200',
  'Most complete package': 'bg-teal-100 text-teal-700 border-teal-200',
};

const PRIORITY_FACTORS = [
  { key: 'price', label: 'Price', desc: 'Lower price scores higher' },
  { key: 'response_speed', label: 'Fast Response', desc: 'Quicker clinic replies' },
  { key: 'rating', label: 'Clinic Rating', desc: 'Higher rated clinics' },
  { key: 'package_completeness', label: 'Package Detail', desc: 'More itemized services' },
  { key: 'treatment_specialization', label: 'Specialization', desc: 'Treatment expertise' },
];

const DEFAULT_PRIORITIES = {
  price: 0.25, response_speed: 0.20, rating: 0.20,
  package_completeness: 0.20, treatment_specialization: 0.15,
};

const PRESETS = [
  {
    id: 'budget',
    label: 'Budget Traveler',
    icon: '💰',
    weights: { price: 0.45, response_speed: 0.15, rating: 0.10, package_completeness: 0.20, treatment_specialization: 0.10 },
  },
  {
    id: 'premium',
    label: 'Premium Experience',
    icon: '✦',
    weights: { price: 0.10, response_speed: 0.15, rating: 0.30, package_completeness: 0.25, treatment_specialization: 0.20 },
  },
  {
    id: 'fast',
    label: 'Fast Track',
    icon: '⚡',
    weights: { price: 0.15, response_speed: 0.40, rating: 0.15, package_completeness: 0.10, treatment_specialization: 0.20 },
  },
];

const STORAGE_KEY = 'treatx_decision_priorities';

function loadSavedPriorities() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed.weights === 'object') return parsed;
    }
  } catch {}
  return null;
}

function savePriorities(weights, presetId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ weights, presetId }));
  } catch {}
}

// TREATX-BATCH-1-CANONICAL-SSOT — Offer Comparison consumes the
// same backend-authoritative `financial_summary.total` used by the
// Offers List and Offer Detail. The prior client-side aggregator
// (sum of `offer_items[].price` OR fallback to `base_price`) has
// been physically removed: it constituted a parallel financial
// truth that could disagree with the backend / PDF. If the backend
// has not yet emitted `financial_summary` for a legacy cached row,
// we render `—` rather than a fabricated total.
function canonicalTotal(quote) {
  const fs = quote?.financial_summary;
  if (fs && Number.isFinite(fs.total)) return fs.total;
  return null;
}

function latestPerClinic(quotes) {
  const map = {};
  for (const q of quotes) {
    const cid = q.clinic_id;
    if (!map[cid] || (q.version_number || 0) > (map[cid].version_number || 0)) {
      map[cid] = q;
    }
  }
  return Object.values(map);
}

/* ── Score Breakdown Bar ────────────────────────────────── */
function ScoreBreakdown({ breakdown, rawScores }) {
  if (!breakdown) return null;
  const factors = PRIORITY_FACTORS;
  return (
    <div className="space-y-1.5 mt-2" data-testid="score-breakdown">
      {factors.map(f => {
        const contrib = breakdown[f.key] || 0;
        const raw = rawScores?.[f.key] || 0;
        return (
          <div key={f.key} className="flex items-center gap-2 text-[10px]">
            <span className="w-24 text-slate-500 truncate">{f.label}</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#3BA4C5] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(raw, 100)}%` }}
              />
            </div>
            <span className="w-8 text-right font-medium text-slate-600">{contrib.toFixed(0)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Priority Slider Panel ──────────────────────────────── */
function PriorityPanel({ priorities, onChange, onReset, loading, activePreset, onPresetSelect }) {
  const { t } = useTranslation();
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm"
      data-testid="priority-panel"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-[#3BA4C5]" />
          <span className="text-sm font-semibold text-[#122033]">{t('patient.journey.yourPriorities')}</span>
        </div>
        <button
          onClick={onReset}
          className="text-[10px] text-slate-400 hover:text-[#3BA4C5] transition-colors"
          data-testid="priority-reset-btn"
        >
          Reset defaults
        </button>
      </div>

      {/* Preset selector */}
      <div className="px-4 pt-3 pb-2">
        <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">{t('patient.journey.chooseProfile')}</p>
        <div className="flex gap-2" data-testid="preset-selector">
          {PRESETS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPresetSelect(p)}
              className={`flex-1 px-2 py-2 rounded-lg border text-[11px] font-medium transition-all
                ${activePreset === p.id
                  ? 'border-[#3BA4C5] bg-[#3BA4C5]/10 text-[#3BA4C5]'
                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100'
                }`}
              data-testid={`preset-${p.id}`}
            >
              <span className="block text-sm mb-0.5">{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active preset label */}
      {activePreset && (
        <div className="mx-4 mt-1 mb-0 px-2.5 py-1.5 bg-[#3BA4C5]/5 border border-[#3BA4C5]/20 rounded-md" data-testid="active-preset-label">
          <p className="text-[10px] text-[#3BA4C5] font-medium">
            Insights based on {PRESETS.find(p => p.id === activePreset)?.label} priorities
          </p>
        </div>
      )}

      <div className="px-4 py-3 space-y-3">
        {PRIORITY_FACTORS.map(f => {
          const val = priorities[f.key] || 0;
          return (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-700">{f.label}</label>
                <span className="text-[10px] text-slate-400">{Math.round(val * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={Math.round(val * 100)}
                onChange={e => onChange(f.key, parseInt(e.target.value) / 100)}
                className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#3BA4C5]
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#3BA4C5] [&::-webkit-slider-thumb]:shadow-md
                  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
                data-testid={`priority-slider-${f.key}`}
              />
              <p className="text-[9px] text-slate-400 mt-0.5">{f.desc}</p>
            </div>
          );
        })}
        {loading && (
          <div className="flex items-center justify-center py-1 text-[10px] text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin mr-1" /> Updating rankings...
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Offer Column (extended with insights) ──────────────── */
function OfferColumn({ quote, rank, insightLabels, decisionScore, breakdown, rawScores, onAccept, onReject, onAskQuestion, actionLoading }) {
  const { t } = useTranslation();
  const total = canonicalTotal(quote);
  const currency = quote.financial_summary?.currency || quote.currency || '';
  const expired = quote.valid_until && new Date(quote.valid_until) < new Date();
  const canAct = quote.quote_actionable !== undefined ? quote.quote_actionable : ((quote.status === 'SENT' || quote.status === 'VIEWED') && !expired); // LIFECYCLE-V3.2
  const [showBreakdown, setShowBreakdown] = useState(false);
  const isBestMatch = insightLabels?.includes('Best match for your priorities');

  return (
    <div
      className={`min-w-[260px] max-w-[340px] flex-1 rounded-xl border-2 transition-all ${
        isBestMatch ? 'border-emerald-400 shadow-lg shadow-emerald-50' : 'border-slate-200'
      }`}
      data-testid={`compare-column-${quote.id}`}
    >
      {/* Rank + Insight Badges */}
      <div className={`px-4 py-3 rounded-t-lg ${isBestMatch ? 'bg-emerald-50' : 'bg-slate-50'}`}>
        {/* Insight badges */}
        {insightLabels?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2" data-testid="insight-badges">
            {insightLabels.map((label, i) => (
              <Badge
                key={i}
                variant="outline"
                className={`text-[9px] px-1.5 py-0.5 ${INSIGHT_STYLE[label] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
                data-testid={`insight-badge-${label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {label}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          {rank != null && (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
              rank === 1 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
            }`} data-testid="offer-rank">
              {rank}
            </div>
          )}
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
            <Building2 className="h-4 w-4 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#122033] truncate" data-testid="compare-clinic-name">
              {quote.clinic_info?.clinic_name || 'Clinic'}
            </p>
            <p className="text-[10px] text-slate-400 truncate">{quote.clinic_info?.country || ''}</p>
          </div>
        </div>
      </div>

      {/* Decision Score */}
      {decisionScore != null && (
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between" data-testid="decision-score-bar">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t('patient.journey.matchScore')}</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  decisionScore >= 70 ? 'bg-emerald-500' : decisionScore >= 40 ? 'bg-amber-400' : 'bg-slate-300'
                }`}
                style={{ width: `${Math.min(decisionScore, 100)}%` }}
              />
            </div>
            <span className={`text-xs font-bold ${
              decisionScore >= 70 ? 'text-emerald-600' : decisionScore >= 40 ? 'text-amber-600' : 'text-slate-500'
            }`}>{decisionScore.toFixed(0)}</span>
          </div>
        </div>
      )}

      {/* Price */}
      <div className="px-4 py-3 border-b border-slate-100 text-center">
        {quote.campaign_id && (
          <Badge className="text-[10px] mb-1.5 bg-rose-100 text-rose-700 border-rose-200" data-testid="campaign-badge">
            International Patient Package
          </Badge>
        )}
        <p className="text-2xl font-bold text-green-700" data-testid="compare-total-price">
          {total != null ? `${sym(currency)}${total.toLocaleString()}` : '—'}
        </p>
        <p className="text-[10px] text-slate-400">{currency} total</p>
        {quote.discount_percentage && (
          <Badge variant="outline" className="text-[10px] mt-1 border-amber-300 text-amber-600">
            {quote.discount_percentage}% discount
          </Badge>
        )}
      </div>

      {/* Offer Items */}
      <div className="px-4 py-3 border-b border-slate-100">
        {(() => {
          // TREATX-BATCH-1.2 O1 — full commercial breakdown so
          // patients can compare offers without opening each
          // detail modal. Every number here is a CONSUMER of the
          // canonical `financial_summary` and the persisted
          // `additional_services` / `included_items` — no
          // client-side financial arithmetic. Base Price is
          // rendered as an explicit priced line item labeled
          // `<Treatment Name> (Base Price)` for parity with
          // Offer Detail + PDF.
          const fs = quote.financial_summary || {};
          const sy = sym(fs.currency || currency);
          const basePrice = Number.isFinite(fs.base_price) ? fs.base_price : null;
          const addTotal = Number.isFinite(fs.additional_services_total) ? fs.additional_services_total : 0;
          const additionalSvcs = quote.additional_services || [];
          const treatment = (quote.treatment_name || '').trim();
          const baseLabel = treatment ? `${treatment} (Base Price)` : 'Base Price';
          return (
            <div className="space-y-1.5" data-testid="compare-package-breakdown">
              {/* Base Price */}
              {basePrice != null && (
                <div className="flex justify-between items-baseline text-xs" data-testid="compare-base-price-line">
                  <span className="text-slate-700 min-w-0 flex-1 pr-2 truncate">{baseLabel}</span>
                  <span className="font-medium text-slate-800 shrink-0">
                    {sy}{basePrice.toLocaleString()}
                  </span>
                </div>
              )}
              {/* Additional services with qty / unit / line total */}
              {additionalSvcs.length > 0 && (
                <>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1.5">Additional Services</p>
                  {additionalSvcs.map((svc, i) => {
                    const qty = Number(svc.quantity) || 1;
                    const unit = Number(svc.price) || 0;
                    const line = qty * unit;
                    return (
                      <div key={i} className="flex justify-between items-baseline text-[11px]" data-testid={`compare-add-svc-${i}`}>
                        <span className="text-slate-600 min-w-0 flex-1 pr-2 truncate">
                          {svc.name || 'Service'}
                          {qty > 1 && <span className="text-slate-400"> × {qty}</span>}
                        </span>
                        <span className="text-slate-700 shrink-0">
                          {sy}{line.toLocaleString()}
                          {qty > 1 && (
                            <span className="block text-[9px] text-slate-400 text-right">
                              ({sy}{unit.toLocaleString()} ea)
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-baseline text-xs pt-1 border-t border-slate-100 font-medium" data-testid="compare-add-svc-total">
                    <span className="text-slate-700">Additional Services Total</span>
                    <span className="text-slate-800">{sy}{addTotal.toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Deposit / Balance / Installments */}
      {(() => {
        const fs = quote.financial_summary || {};
        if (!Number.isFinite(fs.deposit) && !Number.isFinite(fs.balance)) return null;
        const sy = sym(fs.currency || currency);
        return (
          <div className="px-4 py-3 border-b border-slate-100 space-y-1" data-testid="compare-payment-terms">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Payment Terms</p>
            {Number.isFinite(fs.deposit) && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">
                  Deposit{quote.deposit_type === 'percentage' && quote.deposit_value != null
                    ? ` (${quote.deposit_value}%)` : ''}
                </span>
                <span className="font-medium text-slate-800" data-testid="compare-deposit">
                  {/* TREATX-BATCH-B1.2 Item 4 — 2-decimal fractional money. */}
                  {formatMoneyPrecise(fs.deposit, currency)}
                </span>
              </div>
            )}
            {Number.isFinite(fs.balance) && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">
                  Balance{fs.payment_structure === 'installments' && fs.installment_count
                    ? ` (${fs.installment_count} × ${formatMoneyPrecise(Number(fs.per_installment_amount || 0), currency)})`
                    : ''}
                </span>
                <span className="font-medium text-slate-800" data-testid="compare-balance">
                  {formatMoneyPrecise(fs.balance, currency)}
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Travel & Accommodation */}
      {(quote.hotel_nights || quote.hotel_type || quote.airport_transfer || quote.proposed_dates) && (
        <div className="px-4 py-3 border-b border-slate-100" data-testid="compare-travel-block">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Travel &amp; Accommodation</p>
          <div className="space-y-0.5 text-xs text-slate-600">
            {quote.hotel_nights ? <div>Hotel: {quote.hotel_nights} nights{quote.hotel_type ? ` · ${quote.hotel_type}` : ''}</div> : null}
            {quote.airport_transfer ? <div>Airport transfer included</div> : null}
            {quote.proposed_dates ? <div>Proposed dates: {quote.proposed_dates}</div> : null}
          </div>
        </div>
      )}

      {/* Legacy Offer Items (quote_response schema) */}
      {quote.offer_items?.length > 0 && (
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="space-y-1.5" data-testid="compare-offer-items">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{t('patient.journey.itemizedBreakdown')}</p>
            {quote.offer_items.map((item, i) => (
              <div key={i} className="flex justify-between items-start text-xs" data-testid={`compare-item-${i}`}>
                <div className="min-w-0 flex-1">
                  <span className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium capitalize">
                    {(item.item_type || '').replace(/_/g, ' ')}
                  </span>
                  {item.description && (
                    <p className="text-[10px] text-slate-400 mt-0.5 truncate">{item.description}</p>
                  )}
                </div>
                <span className="font-medium text-slate-700 ml-2 shrink-0">
                  {sym(item.currency || currency)}{(item.price || 0).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inclusions */}
      {quote.included_items?.length > 0 && (
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{t('patient.journey.inclusionsHeader')}</p>
          <div className="space-y-1">
            {quote.included_items.map((item, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                <Check className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                <span>{typeof item === 'string' ? item : item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {quote.notes_patient_visible && (
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{t('patient.journey.notesHeader')}</p>
          <p className="text-xs text-slate-600">{quote.notes_patient_visible}</p>
        </div>
      )}

      {/* Collapsible Score Breakdown */}
      {breakdown && (
        <div className="px-4 py-2 border-b border-slate-100">
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center gap-1 text-[10px] text-[#3BA4C5] hover:text-[#2d8aa8] w-full"
            data-testid="toggle-breakdown"
          >
            <Info className="h-3 w-3" />
            {showBreakdown ? 'Hide' : 'Show'} score details
            {showBreakdown ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
          </button>
          {showBreakdown && <ScoreBreakdown breakdown={breakdown} rawScores={rawScores} />}
        </div>
      )}

      {/* Status & Validity */}
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <Badge className={`text-[10px] ${STATUS_STYLE[quote.status] || 'bg-slate-100 text-slate-500'}`} data-testid="compare-status">
            {t(`patient.offers.status.${quote.status}`, { defaultValue: quote.status })}
          </Badge>
          <span className="text-[10px] text-slate-400">v{quote.version_number || 1}</span>
        </div>
        {quote.valid_until && (
          <div className={`flex items-center gap-1 text-[10px] ${expired ? 'text-red-500' : 'text-slate-400'}`}>
            <Clock className="h-3 w-3" />
            {expired ? 'Expired' : `Valid until ${formatDateLocale(quote.valid_until)}`}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-slate-100">
        {canAct ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1 text-xs"
              onClick={() => onReject(quote.id)} disabled={actionLoading === quote.id}
              data-testid={`compare-reject-${quote.id}`}>
              Decline
            </Button>
            <Button size="sm" className="flex-1 text-xs bg-green-600 hover:bg-green-700"
              onClick={() => onAccept(quote.id)} disabled={actionLoading === quote.id}
              data-testid={`compare-accept-${quote.id}`}>
              {actionLoading === quote.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle className="h-3 w-3 mr-1" /> Accept</>}
            </Button>
          </div>
        ) : quote.status === 'ACCEPTED' ? (
          <div className="text-center" data-testid={`compare-accepted-${quote.id}`}>
            <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Accepted</Badge>
          </div>
        ) : quote.status === 'REJECTED' ? (
          <div className="text-center" data-testid={`compare-rejected-${quote.id}`}>
            <Badge className="bg-slate-100 text-slate-500"><XCircle className="h-3 w-3 mr-1" /> Declined</Badge>
          </div>
        ) : expired ? (
          <p className="text-center text-[10px] text-red-500">{t('patient.offers.expired')}</p>
        ) : null}
        {/* Ask clinic a question */}
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 text-xs text-[#3BA4C5] border-[#3BA4C5]/30 hover:bg-[#3BA4C5]/5"
          onClick={() => onAskQuestion(quote)}
          data-testid={`ask-clinic-btn-${quote.id}`}
        >
          <MessageCircle className="h-3 w-3 mr-1" /> Ask clinic a question
        </Button>
      </div>
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────── */
export default function OfferComparisonPage() {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allQuotes, setAllQuotes] = useState([]);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAcceptQuoteId, setConfirmAcceptQuoteId] = useState(null);

  // Decision engine state
  const saved = useMemo(() => loadSavedPriorities(), []);
  const [priorities, setPriorities] = useState(saved?.weights || { ...DEFAULT_PRIORITIES });
  const [activePreset, setActivePreset] = useState(saved?.presetId || null);
  const [rankings, setRankings] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showPriorityPanel, setShowPriorityPanel] = useState(false);

  // Block clinic users
  useEffect(() => {
    if (user && ['provider_admin', 'provider_coordinator', 'provider_sales_rep'].includes(user.role)) {
      toast.error('Clinic users cannot access offer comparison');
      navigate('/clinic/admin', { replace: true });
    }
  }, [user, navigate]);

  const getAuthHeaders = useCallback(() => {
    const token = '';
    return {
      'Content-Type': 'application/json',
      
    };
  }, []);

  const fetchQuotes = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    const queryId = user.person_id || user.id;
    try {
      const res = await api.get(`/patient/quotes/${queryId}`);
      const data = res.data;
      setAllQuotes(data.quotes || []);
    } catch (e) {
      console.error('Fetch quotes error:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.person_id, user?.id, getAuthHeaders]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Fetch decision insights whenever priorities change
  const fetchInsights = useCallback(async () => {
    if (!leadId) return;
    setInsightsLoading(true);
    try {
      const res = await api.post(`/patient/offers/${leadId}/decision-insights`, { patient_priorities: priorities });
      const data = res.data;
      if (data.success) setRankings(data.rankings);
    } catch (e) {
      console.error('Decision insights error:', e);
    } finally {
      setInsightsLoading(false);
    }
  }, [leadId, priorities, getAuthHeaders]);

  // Debounced fetch on priority change
  useEffect(() => {
    const timer = setTimeout(() => { fetchInsights(); }, 300);
    return () => clearTimeout(timer);
  }, [fetchInsights]);

  const handlePriorityChange = (key, value) => {
    setPriorities(prev => {
      const next = { ...prev, [key]: value };
      savePriorities(next, null);
      return next;
    });
    setActivePreset(null); // manual change clears preset
  };

  const handlePresetSelect = (preset) => {
    setPriorities({ ...preset.weights });
    setActivePreset(preset.id);
    savePriorities(preset.weights, preset.id);
  };

  const handleResetPriorities = () => {
    setPriorities({ ...DEFAULT_PRIORITIES });
    setActivePreset(null);
    savePriorities(DEFAULT_PRIORITIES, null);
  };

  const handleAskQuestion = async (quote) => {
    // TREATX-BATCH-1.2 C2 — route through the canonical messaging
    // bridge exactly like Patient Offer Detail → Open Messages.
    // Prior code sent `thread_type='offer_discussion'` (not a
    // valid ThreadType enum member — immediate 422) and
    // `context_id=quote.lead_id` (which the offer-context
    // resolver interprets as a quote id and fails to find).
    // Both patient entry points now open the SAME canonical
    // patient-provider thread; no shadow Compare-specific
    // messaging system.
    try {
      const res = await api.post('/v2/threads', {
        thread_type: 'offer',
        context_id: quote.id,
        context_type: 'offer',
        clinic_id: quote.clinic_id,
        subject: `Offer: ${quote.treatment_name || quote.clinic_name || 'clinic'}`,
      });
      const data = res.data;
      const threadId = data.thread?.id || data.id || data.thread_id;
      if (threadId) {
        navigate(`/patient/messages?thread=${threadId}`);
      } else {
        navigate('/patient/messages');
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not open messaging thread'));
    }
  };

  const handleAccept = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const res = await api.post(`/patient/quotes/${quoteId}/accept`, {});
      const data = res.data;
      // P0-PAY-STUCK-01 — acceptance alone does not hold or confirm anything;
      // the deposit does. Do not imply a reservation before payment.
      toast.success(`Offer accepted.${data.auto_rejected_count ? ` ${data.auto_rejected_count} other offer(s) auto-declined.` : ''} Next step: the deposit, which confirms your place.`);
      setConfirmAcceptQuoteId(null);
      // QOF-03 — hand the patient straight to the payment / next-step screen.
      const orderId = data?.order?.id;
      const qs = new URLSearchParams();
      qs.set('quote_id', quoteId);
      if (orderId) qs.set('order_id', orderId);
      navigate(`/patient/payment?${qs.toString()}`);
    } catch (e) {
      // P0-DOB-SELF-DECLARATION-01 — structured eligibility errors carry the
      // actionable reason; send the patient to the offer page where the
      // canonical date-of-birth form lives.
      const code = apiErrorCode(e);
      toast.error(apiErrorMessage(e, t('patient.quote.acceptFailed')));
      if (ELIGIBILITY_CODES.includes(code)) navigate(`/patient/quotes/${quoteId}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await api.post(`/patient/quotes/${quoteId}/reject`, {});
      toast.success('Offer declined.');
      await fetchQuotes();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('patient.quote.rejectFailed')));
    } finally {
      setActionLoading(null);
    }
  };

  const leadQuotes = useMemo(() => allQuotes.filter(q => q.lead_id === leadId), [allQuotes, leadId]);

  const displayQuotes = useMemo(() => {
    if (showAllVersions) return leadQuotes;
    return latestPerClinic(leadQuotes);
  }, [leadQuotes, showAllVersions]);

  // Merge rankings with display quotes: re-order by decision_score
  const rankedQuotes = useMemo(() => {
    if (!rankings || rankings.length === 0) return displayQuotes;
    const rankMap = {};
    rankings.forEach(r => { rankMap[r.quote_id] = r; });
    // Sort display quotes by their ranking (ranked first, unranked last)
    const sorted = [...displayQuotes].sort((a, b) => {
      const ra = rankMap[a.id]?.decision_score ?? -1;
      const rb = rankMap[b.id]?.decision_score ?? -1;
      return rb - ra;
    });
    return sorted;
  }, [displayQuotes, rankings]);

  const rankMap = useMemo(() => {
    const map = {};
    if (rankings) rankings.forEach(r => { map[r.quote_id] = r; });
    return map;
  }, [rankings]);

  const treatmentName = leadQuotes[0]?.treatment_name || 'Treatment';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="comparison-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (leadQuotes.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="comparison-empty">
        <Card className="max-w-sm text-center p-8">
          <Scale className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <h2 className="font-semibold text-lg mb-1">{t('patient.offerCompare.empty').split('.')[0]}</h2>
          <p className="text-sm text-slate-400 mb-4">{t('patient.offerCompare.empty')}</p>
          <Button variant="outline" onClick={() => navigate('/patient/offers')}>{t('patient.journey.backToOffers')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="offer-comparison-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#122033] to-[#1a3352] text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/patient/offers')}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="comparison-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Offers
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Scale className="h-6 w-6 text-blue-300" />
              <div>
                <h1 className="text-xl font-semibold">{t('patient.offerCompare.title')}</h1>
                <p className="text-sm text-blue-200">{treatmentName} — {rankedQuotes.length} offer{rankedQuotes.length !== 1 ? 's' : ''}</p>
                {language !== 'en' && <TranslatedFromEnglishBadge currentLanguage={language} className="mt-1" />}
              </div>
            </div>
            {/* Toggle Decision Insights */}
            {rankedQuotes.length >= 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPriorityPanel(!showPriorityPanel)}
                className={`text-xs gap-1.5 ${showPriorityPanel ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                data-testid="toggle-insights-btn"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {showPriorityPanel ? 'Hide' : 'Show'} Decision Insights
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* M16B-07B — Treatment Ready (also covers revision-history view; this panel renders per-treatment chips for the latest active offer on this lead) */}
        <div className="mb-4">
          <TreatmentReadyPanel leadId={leadId} mode="patient" title={t('patient.treatmentReady.title')} />
        </div>
        {/* Controls */}
        {leadQuotes.length !== latestPerClinic(leadQuotes).length && (
          <div className="mb-4 flex items-center gap-3" data-testid="version-toggle">
            <button
              onClick={() => setShowAllVersions(!showAllVersions)}
              className="flex items-center gap-1 text-xs text-[#3BA4C5] hover:underline"
              data-testid="toggle-all-versions"
            >
              {showAllVersions ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showAllVersions ? 'Show latest per clinic only' : `Show all versions (${leadQuotes.length})`}
            </button>
          </div>
        )}

        <div className={`flex gap-6 ${showPriorityPanel ? 'flex-col lg:flex-row' : ''}`}>
          {/* Priority Panel (collapsible sidebar) */}
          {showPriorityPanel && rankedQuotes.length >= 2 && (
            <div className="w-full lg:w-72 lg:shrink-0">
              <PriorityPanel
                priorities={priorities}
                onChange={handlePriorityChange}
                onReset={handleResetPriorities}
                loading={insightsLoading}
                activePreset={activePreset}
                onPresetSelect={handlePresetSelect}
              />
            </div>
          )}

          {/* Comparison columns */}
          <div className="flex-1">
            <div className="flex gap-4 overflow-x-auto pb-4" data-testid="comparison-columns">
              {rankedQuotes.map((q, idx) => {
                const r = rankMap[q.id];
                return (
                  <OfferColumn
                    key={q.id}
                    quote={q}
                    rank={rankings && rankings.length >= 2 ? idx + 1 : null}
                    insightLabels={r?.insight_labels}
                    decisionScore={r?.decision_score}
                    breakdown={r?.decision_breakdown}
                    rawScores={r?.raw_scores}
                    onAccept={(id) => setConfirmAcceptQuoteId(id)}
                    onReject={handleReject}
                    onAskQuestion={handleAskQuestion}
                    actionLoading={actionLoading}
                  />
                );
              })}
            </div>

            {rankedQuotes.length === 1 && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center" data-testid="single-offer-notice">
                <p className="text-sm text-amber-700">Only one offer exists for this lead. Decision insights require 2+ offers from different clinics.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Accept Confirmation Modal */}
      {confirmAcceptQuoteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="compare-accept-confirm-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#122033]">Accept this offer?</h3>
            </div>
            <p className="text-sm text-slate-500">
              Accepting this offer will automatically decline all other offers for this request. This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmAcceptQuoteId(null)}
                data-testid="compare-accept-cancel">
                Cancel
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleAccept(confirmAcceptQuoteId)}
                disabled={actionLoading === confirmAcceptQuoteId}
                data-testid="compare-accept-yes">
                {actionLoading === confirmAcceptQuoteId ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Yes, Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
